"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useTransition,
  type ReactNode,
} from "react";
import { useRouter, usePathname } from "@/i18n/navigation";
import { LoadingOverlay } from "@/components/ui/loading-overlay";

interface NavigationContextType {
  isNavigating: boolean;
  navigateTo: (path: string) => void;
  startLoading: () => void;
  stopLoading: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const [isManualLoading, setIsManualLoading] = useState(false);
  const [targetPath, setTargetPath] = useState<string | null>(null);

  const isNavigating = isPending || isManualLoading;

  const navigateTo = useCallback(
    (path: string) => {
      setTargetPath(path);
      setIsManualLoading(true);
      startTransition(() => {
        router.push(path);
      });
    },
    [router, startTransition],
  );

  const startLoading = useCallback(() => {
    setIsManualLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setIsManualLoading(false);
    setTargetPath(null);
  }, []);

  useEffect(() => {
    if (targetPath && pathname.includes(targetPath.split("?")[0])) {
      setIsManualLoading(false);
      setTargetPath(null);
    }
  }, [pathname, targetPath]);

  useEffect(() => {
    if (!isPending && isManualLoading) {
      const timeout = setTimeout(() => {
        setIsManualLoading(false);
        setTargetPath(null);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isPending, isManualLoading]);

  return (
    <NavigationContext.Provider
      value={{ isNavigating, navigateTo, startLoading, stopLoading }}
    >
      {children}
      <LoadingOverlay isVisible={isNavigating} />
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within NavigationProvider");
  }
  return context;
}
